import { useTranslation } from 'react-i18next';
import { BookHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SectionHeading } from './SectionHeading';

interface HowItWorksSectionProps {
  /** The backend takes photos (limits.maxPhotos > 0). */
  photos: boolean;
  /** The printed book can be ordered. */
  print: boolean;
  /** Free cover redraws before paying (config.redraws.max). */
  coverRedraws: number;
  onStart: () => void;
}

/** The real flow, start to finish: tell, photos, free preview, buy and check, keep. */
export function HowItWorksSection({ photos, print, coverRedraws, onStart }: HowItWorksSectionProps) {
  const { t } = useTranslation();
  const steps = [
    { key: 'tell', emoji: '✏️', body: t('storybookPage.how.steps.tell.body') },
    ...(photos ? [{ key: 'photos', emoji: '📸', body: t('storybookPage.how.steps.photos.body') }] : []),
    {
      key: 'preview',
      emoji: '📖',
      body: t(coverRedraws > 0 ? 'storybookPage.how.steps.preview.body' : 'storybookPage.how.steps.preview.bodyNoRedraw'),
    },
    { key: 'check', emoji: '🔍', body: t('storybookPage.how.steps.check.body') },
    // "A keepsake for years" is the printed book's promise; a PDF is simply yours to keep.
    {
      key: 'keep',
      emoji: '🎁',
      title: t(print ? 'storybookPage.how.steps.keep.title' : 'storybookPage.how.steps.keep.titlePdf'),
      body: t(print ? 'storybookPage.how.steps.keep.body' : 'storybookPage.how.steps.keep.bodyPdf'),
    },
  ];

  return (
    <section id="how-it-works" className="scroll-mt-20 py-14 sm:py-20" aria-labelledby="storybook-how-title">
      <div className="container mx-auto px-4">
        <SectionHeading id="storybook-how-title" eyebrow={t('storybookPage.how.eyebrow')} title={t('storybookPage.how.title')} />
        <ol className="mx-auto grid max-w-6xl gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-5">
          {steps.map((step, index) => (
            <li key={step.key} className="glass-card relative flex gap-4 p-4 sm:p-5 lg:flex-col lg:items-center lg:p-6 lg:text-center">
              <span aria-hidden="true" className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl bg-muted text-3xl lg:h-16 lg:w-16 lg:text-4xl">
                {step.emoji}
              </span>
              <span className="min-w-0">
                <span className="block text-xs font-bold uppercase tracking-wider text-primary">
                  {t('storybookPage.how.stepLabel', { n: index + 1 })}
                </span>
                <h3 className="mt-1 text-lg font-semibold leading-snug lg:text-xl">{'title' in step ? step.title : t(`storybookPage.how.steps.${step.key}.title`)}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
              </span>
            </li>
          ))}
        </ol>
        <div className="mt-8 flex justify-center">
          <Button
            size="lg"
            className="h-auto min-h-12 w-full whitespace-normal rounded-full py-3 text-center leading-snug sm:w-auto"
            onClick={onStart}
          >
            <BookHeart aria-hidden="true" />
            {t('storybookPage.hero.cta')}
          </Button>
        </div>
      </div>
    </section>
  );
}
